import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useSession } from '../state/session';

export const BRAND_NAME = 'eSignLoop';

/** The loop mark: a closed circuit, for send → sign → preserve → verify. */
export function BrandMark({ size = 26 }: { size?: number }) {
  return (
    <span className="brand__mark" aria-hidden="true"
          style={{ width: size, height: size, borderRadius: size * 0.27 }}>
      <svg viewBox="0 0 24 24" width={size * 0.62} height={size * 0.62}
           fill="none" stroke="currentColor" strokeWidth="2.4"
           strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.5 12a8.5 8.5 0 1 1-2.9-6.4" />
        <path d="M20.5 3.4v4.3h-4.3" />
      </svg>
    </span>
  );
}

export function Brand({ to = '/' }: { to?: string }) {
  return (
    <Link className="brand" to={to} aria-label={BRAND_NAME}>
      <BrandMark />
      {/* The wordmark gives way to the mark alone on very narrow screens so the
          primary action and the menu button always fit. */}
      <span className="brand__name">{BRAND_NAME}</span>
    </Link>
  );
}

type NavItem = { to: string; label: string; end?: boolean };

export function AppShell({ children, nav = [], actions }: {
  children: ReactNode; nav?: NavItem[]; actions?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { user, org, organizations, mailSimulated, selectOrg, signOut } = useSession();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!open && !accountOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      setAccountOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, accountOpen]);

  // Dismiss the account menu on any click outside it.
  useEffect(() => {
    if (!accountOpen) return;
    const onDown = (event: MouseEvent) => {
      if (!accountRef.current?.contains(event.target as Node)) setAccountOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [accountOpen]);

  return (
    <div className="page">
      <a className="skip-link" href="#main">Skip to main content</a>
      <header className="topbar">
        <div className="shell topbar__inner">
          <Brand to={user ? '/app' : '/'} />
          <nav className="navlinks" aria-label="Primary">
            {nav.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className="navlink">
                {item.label}
              </NavLink>
            ))}
          </nav>
          <span className="spacer" />
          <div className="row row--tight" style={{ flexWrap: 'nowrap' }}>
            {actions}
            {user && (
              /* Desktop only. The hamburger is display:none above 900px, so
                 everything that lived behind it -- including sign out -- was
                 unreachable on a laptop. */
              <div className="account" ref={accountRef}>
                <button
                  type="button"
                  className="btn btn--ghost btn--small account__button"
                  aria-expanded={accountOpen}
                  aria-haspopup="menu"
                  onClick={() => setAccountOpen((current) => !current)}
                >
                  <span className="account__avatar" aria-hidden="true">
                    {(user.name || user.email).trim().charAt(0).toUpperCase()}
                  </span>
                  <span className="account__label">{user.email}</span>
                  <span aria-hidden="true" className="account__chev">▾</span>
                </button>
                {accountOpen && (
                  <div className="account__menu" role="menu">
                    <p className="account__who">
                      {user.name ? <strong>{user.name}</strong> : null}
                      <span className="small muted">{user.email}</span>
                    </p>
                    {organizations.length > 1 && (
                      <>
                        <hr className="divider" />
                        <label className="field__label" htmlFor="org-switch-desktop">
                          Organization
                        </label>
                        <select id="org-switch-desktop" className="select"
                                value={org?.id || ''}
                                onChange={(event) => selectOrg(event.target.value)}>
                          {organizations.map((item) => (
                            <option key={item.id} value={item.id}>{item.name}</option>
                          ))}
                        </select>
                      </>
                    )}
                    <hr className="divider" />
                    <button type="button" role="menuitem"
                            className="btn btn--block btn--small"
                            onClick={() => { setAccountOpen(false); void signOut(); }}>
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            )}
            {nav.length > 0 && (
              <button
                type="button"
                className="btn btn--icon btn--ghost menu-button"
                aria-expanded={open}
                aria-label={open ? 'Close menu' : 'Open menu'}
                onClick={() => setOpen((current) => !current)}
              >
                <span aria-hidden="true">{open ? '✕' : '☰'}</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {open && (
        <div className="drawer" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setOpen(false);
        }}>
          <div className="drawer__panel">
            <div className="row row--between" style={{ marginBottom: 12 }}>
              <strong>Menu</strong>
              <button type="button" className="btn btn--small btn--ghost"
                      onClick={() => setOpen(false)}>Close</button>
            </div>
            <nav aria-label="Mobile">
              {nav.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end} className="drawer__link">
                  {item.label}
                </NavLink>
              ))}
            </nav>
            {organizations.length > 1 && (
              <>
                <hr className="divider" />
                <label className="field__label" htmlFor="org-switch">Organization</label>
                <select id="org-switch" className="select" value={org?.id || ''}
                        onChange={(event) => selectOrg(event.target.value)}>
                  {organizations.map((item) => (
                    <option key={item.id} value={item.id}>{item.name}</option>
                  ))}
                </select>
              </>
            )}
            {user && (
              <>
                <hr className="divider" />
                <p className="small muted" style={{ margin: '0 0 10px' }}>
                  Signed in as {user.email}
                </p>
                <button type="button" className="btn btn--block" onClick={() => void signOut()}>
                  Sign out
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <main id="main" style={{ flex: 1 }}>{children}</main>

      <footer className="footer">
        <div className="shell stack-s">
          <div className="row row--between">
            <span>© {new Date().getFullYear()} Verticos.ai LLC</span>
            <span className="row row--tight">
              <Link to="/verify">Verify a document</Link>
              <span aria-hidden="true">·</span>
              <Link to="/pricing">Pricing</Link>
              <span aria-hidden="true">·</span>
              <Link to="/terms">Terms</Link>
              <span aria-hidden="true">·</span>
              <Link to="/privacy">Privacy</Link>
              <span aria-hidden="true">·</span>
              <Link to="/support">Support</Link>
              <span aria-hidden="true">·</span>
              <Link to="/blog">Blog</Link>
              {/* Only offered when mail is simulated — that is the condition
                  under which there is anything in the mailbox to read, and the
                  condition under which showing unredacted signing links is
                  safe. */}
              {mailSimulated && (
                <>
                  <span aria-hidden="true">·</span>
                  <Link to="/dev/mailbox">Dev mailbox</Link>
                </>
              )}
            </span>
          </div>
          <p className="small" style={{ margin: 0, maxWidth: '78ch' }}>
            Electronic signing with SHA-256 integrity records and protected audit
            evidence. No cryptographic PDF seal, externally trusted certificate or
            independent trusted timestamp is applied at this assurance level. This
            product does not provide legal advice, and it makes no claim that any
            particular agreement is enforceable in a given jurisdiction.
          </p>
        </div>
      </footer>
    </div>
  );
}
